import React, {
    useContext,
    useEffect,
    useState,
    useCallback,
    createContext,
} from "react";
import { useAuth } from "hooks/Auth";
import { apiGet } from "helpers/Request";
import { transform as entryTransformer } from "schemas/Entry";
import { transform as channelTransformer } from "schemas/Channel";
import { getPaginationMeta } from "schemas/Util";
import MainBlock from "components/DesignSystem/LayoutBlocks/MainBlock";
import { useLocation, useHistory } from "react-router-dom";
import Pagination from "components/General/Pagination";
import { AppContext } from "context/App";
import EntryTable from "./EntryTable";
import EntryListToolbar from "./EntryListToolbar";
import ButtonGroup from "components/Buttons/ButtonGroup";
import { forceInt } from "Utils";

export const EntryListContext = createContext();

export default function EntryScreen(props) {
    const auth = useAuth();
    const location = useLocation();
    const history = useHistory();
    const { setCurrentNav, setEntryNav } = useContext(AppContext);

    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [channels, setChannels] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [paginationMeta, setPaginationMeta] = useState({});
    const [isFiltered, setIsFiltered] = useState(false);
    const [queryParams, setQueryParams] = useState(
        new URLSearchParams(location.search)
    );

    const setSelected = (e, id) => {
        if (e.target.checked) {
            setSelectedItems([...selectedItems, id]);
        } else {
            setSelectedItems(selectedItems.filter((item) => item !== id));
        }
    };

    const updateParams = (key, val = null) => {
        const params = new URLSearchParams(location.search);

        if (typeof key === "object") {
            Object.entries(key).forEach(([k, v]) => {
                params.set(k, v);
            });
        } else {
            if (params.has(key) && params.get(key) === val) {
                params.delete(key);
            } else {
                params.set(key, val);
            }
        }

        history.replace(location.pathname + "?" + params.toString());
    };

    const load = useCallback(() => {
        setLoading(true);
        setItems([]);
        setSelectedItems([]);
        const defaultParams = {
            per_page: 40,
            dir: "asc",
            order: "title",
            include: ["channel"],
        };
        const definedParams = Object.fromEntries(
            new URLSearchParams(location.search)
        );

        apiGet(
            "/v4/entry",
            auth.publication,
            Object.assign({}, defaultParams, definedParams)
        )
            .then((res) => {
                setItems(entryTransformer(res));
                setPaginationMeta(getPaginationMeta(res));
                setLoading(false);
            })
            .then(() => {
                return apiGet("/v4/channel", auth.publication, {
                    order: "title",
                    dir: "asc",
                    per_page: 200,
                }).then((res) => {
                    setChannels(channelTransformer(res));
                });
            });
    }, [location, auth.publication]);

    useEffect(() => {
        load();
        const params = new URLSearchParams(location.search);
        setQueryParams(params);
        setEntryNav(location.pathname + "?" + params.toString());
    }, [location, load, setEntryNav]);

    useEffect(() => {
        setCurrentNav("custom-content");
    }, [setCurrentNav]);

    useEffect(() => {
        setIsFiltered(queryParams.has("channel"));
    }, [queryParams]);

    return (
        <MainBlock>
            <EntryListContext.Provider
                value={{ items, selectedItems, loading, load }}
            >
                <EntryListToolbar queryParams={queryParams} />
                <div className="my-4 flex flex-wrap justify-center gap-2 sm:justify-start">
                    <div>
                        <ButtonGroup
                            variant="xs"
                            buttons={[
                                <button
                                    type="menu"
                                    key="set-channel"
                                    buttons={channels.map((channel, index) => (
                                        <button
                                            key={index}
                                            onClick={() =>
                                                updateParams({
                                                    channel: channel.id,
                                                    show_hidden: true,
                                                })
                                            }
                                        >
                                            {channel.title}
                                        </button>
                                    ))}
                                >
                                    {queryParams.has("channel")
                                        ? channels.find(
                                              (c) =>
                                                  c.id ===
                                                  forceInt(
                                                      queryParams.get("channel")
                                                  )
                                          )?.title
                                        : "Filter by Channel"}
                                </button>,
                            ]}
                        />
                    </div>
                    <div className={isFiltered ? "" : "hidden"}>
                        <ButtonGroup
                            variant="xs"
                            buttons={[
                                <button
                                    type="button"
                                    key="reset-filters"
                                    onClick={() =>
                                        history.replace(location.pathname)
                                    }
                                >
                                    Reset
                                </button>,
                            ]}
                        />
                    </div>
                    <div className="md:grid md:flex-grow md:justify-items-end">
                        <Pagination
                            variant="xs"
                            maxPages={3}
                            basePath="custom-content"
                            {...paginationMeta}
                        />
                    </div>
                </div>
                <EntryTable
                    addSelected={setSelected}
                    setAllSelected={setSelectedItems}
                    queryParams={queryParams}
                />
            </EntryListContext.Provider>

            <div className="my-4">
                <Pagination basePath="custom-content" {...paginationMeta} />
            </div>
        </MainBlock>
    );
}
